// B5 — space between the photos on a page.
//
// ─── WHAT THIS IS ───────────────────────────────────────────────────────────
//
// A gap between ADJACENT photos on a grid page. Off by default. When on, the
// owner picks the colour and the thickness, per album. The page edges keep
// their full bleed: the guillotine never reaches the interior, so an interior
// gap is always exactly the thickness chosen, while an edge margin would come
// back uneven after trimming and read as a defect.
//
// ─── ONE NUMBER, EVERY SURFACE ──────────────────────────────────────────────
//
// The gap is stored in millimetres because the print is in millimetres. Every
// screen surface derives its pixels from the SAME fraction of the page width:
//
//   print PDF   mm × MM_TO_PT                          (pdfGenerator.ts)
//   editor      mm / 200 × page width in px             (AlbumEditorPage.tsx)
//   thumbnail   mm / 200 × 42 px                        (SpreadThumb)
//   flow cards  calc(page width × mm / 200)             (AlbumFlowPage.tsx)
//   3D preview  — none: it rasterises the print PDF     (previewAssets.ts)
//
// ⚠️ PARITY IS THE POINT. If the gap shows in the editor and not in the
// print, we ship a wrong book — that has been the worst recurring problem in
// this codebase. So the geometry itself is shared too: `slotRects` in
// slotGeometry.ts is byte-identical between web and worker (guarded by a test)
// and takes the gap as an argument. No surface has its own gap arithmetic.
//
// Guard: src/lib/photoGap.parity.test.ts

import { slotRects, WIDE_TOP_MIN_FRAC, WIDE_TOP_MAX_FRAC, usesBaseRules, baseFrameMm, BASE_GAP_MM, BASE_MARGIN_MM, evenFrac, type PageFrame } from './slotGeometry';
import { isThemedBook, spineIndex, type BookPageLike } from './bookStructure';

/** Interior finished trim, mm. Matches PAGE_W / A4_H in pdfGenerator.ts. */
export const PAGE_W_MM = 200;
export const PAGE_H_MM = 297;
/** pdf-lib points per millimetre. Same constant as pdfGenerator.ts. */
export const MM_TO_PT = 2.8346;

/** The owner's range. Above 8 mm the photos stop looking like a grid. */
export const MAX_GAP_MM = 8;
export const DEFAULT_GAP_COLOR = '#FFFFFF';
/**
 * What the assistant sets when a client asks for space and names no size.
 * Three millimetres reads as deliberate at A4 without eating the photos.
 */
export const AGENT_DEFAULT_GAP_MM = 3;

export interface PhotoGap {
  mm: number;
  color: string;
  /** Base layout rules (migration 234): 12/15 mm page margins, and `mm` is
   *  the rules' 4 mm. Absent/false = edge to edge, as before. */
  baseRules?: boolean;
  /** THE BOOK OPENS ON ITS THEMED S1 PAGE (bookStructure.ts): album page 1 is
   *  a RIGHT page, so every page's spine side flips. Absent = the plain book
   *  (album page 1 on the left), exactly as before. Read by the frame helpers
   *  below and by captionLayout.ts. */
  themedBook?: true;
}

/** 0 ≤ mm ≤ MAX, one decimal, and never NaN. */
export function clampGapMm(mm: unknown): number {
  const n = Number(mm);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(MAX_GAP_MM, Math.round(n * 10) / 10);
}

/** `#RRGGBB` uppercase, or the default when the input is not a colour. */
export function normalizeGapColor(hex: unknown): string {
  const s = String(hex ?? '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(s) ? s.toUpperCase() : DEFAULT_GAP_COLOR;
}

/**
 * The page array of a session's `pages` column. The column is an OBJECT —
 * `{ pages: [...], intro_elements, … }` — but an older flat array is accepted
 * too, so a caller never has to know which one it holds.
 */
function bookPagesOf(pages: unknown): ReadonlyArray<BookPageLike | null | undefined> {
  if (Array.isArray(pages)) return pages as BookPageLike[];
  const inner = (pages as { pages?: unknown } | null | undefined)?.pages;
  return Array.isArray(inner) ? inner as BookPageLike[] : [];
}

/** Read the pair off a session row (or anything shaped like one). */
export function photoGapOf(row: { photo_gap_mm?: unknown; photo_gap_color?: unknown; layout_rules?: unknown; pages?: unknown } | null | undefined): PhotoGap {
  const baseRules = usesBaseRules(row?.layout_rules);
  return {
    mm: baseRules ? BASE_GAP_MM : clampGapMm(row?.photo_gap_mm),
    color: normalizeGapColor(row?.photo_gap_color),
    ...(baseRules ? { baseRules: true } : {}),
    ...(isThemedBook(bookPagesOf(row?.pages)) ? { themedBook: true as const } : {}),
  };
}

/**
 * `gap` for a book that is (or is not) themed RIGHT NOW. A screen that edits
 * the pages decides from its current pages, not from the row it loaded: a
 * customer who deletes or moves the S1 opening page gets the plain book back
 * (bookStructure.ts), and every frame must flip with it. Returns `gap` itself
 * when nothing changes, so a plain book keeps the very same object.
 */
export function withThemedBook<T extends Pick<PhotoGap, 'themedBook'>>(gap: T, themed: boolean): T {
  if (!!gap.themedBook === themed) return gap;
  if (themed) return { ...gap, themedBook: true as const };
  const rest = { ...gap };
  delete rest.themedBook;
  return rest;
}

/**
 * The page's margins in mm for album page `pageIndex` (0-based), or null when
 * the album is edge to edge. Every screen surface turns this into padding with
 * the SAME mm/200 × page-width rule as the gap, so editor, thumbnails, flow
 * cards and print agree to the pixel. The 15 mm spine margin sits on the
 * binding side of the PRINTED book — `gap.themedBook` puts album page 1 on
 * the right (bookStructure.ts).
 */
export function pageFrameMm(gap: Pick<PhotoGap, 'baseRules' | 'themedBook'> | null | undefined, pageIndex: number): PageFrame | null {
  // The spine side follows the printed book: a themed book's album page 1 is
  // a RIGHT page (bookStructure.spineIndex). A plain book is unchanged.
  return gap?.baseRules ? baseFrameMm(spineIndex(pageIndex, !!gap.themedBook)) : null;
}

/** Screen: the frame as a CSS `padding` value for a page `pageWidthPx` wide. */
export function framePaddingPx(gap: Pick<PhotoGap, 'baseRules' | 'themedBook'> | null | undefined, pageIndex: number, pageWidthPx: number): string | undefined {
  const f = pageFrameMm(gap, pageIndex);
  if (!f) return undefined;
  const px = (mm: number) => `${(mm / PAGE_W_MM) * (Number.isFinite(pageWidthPx) && pageWidthPx > 0 ? pageWidthPx : 0)}px`;
  return `${px(f.top)} ${px(f.right)} ${px(f.bottom)} ${px(f.left)}`;
}

/** Screen, CSS page width: the frame as a `padding` of `calc()`s. */
export function framePaddingCss(gap: Pick<PhotoGap, 'baseRules' | 'themedBook'> | null | undefined, pageIndex: number, pageWidthCss: string): string | undefined {
  const f = pageFrameMm(gap, pageIndex);
  if (!f) return undefined;
  const c = (mm: number) => `calc(${pageWidthCss} * ${mm / PAGE_W_MM})`;
  return `${c(f.top)} ${c(f.right)} ${c(f.bottom)} ${c(f.left)}`;
}

/** The gap as a fraction of the page width — the one number everything shares. */
export function gapFraction(mm: number): number {
  return clampGapMm(mm) / PAGE_W_MM;
}

/** Print: the gap in pdf-lib points. */
export function gapPt(mm: number): number {
  return clampGapMm(mm) * MM_TO_PT;
}

/** Screen: the gap in pixels for a page rendered `pageWidthPx` wide. */
export function gapPx(mm: number, pageWidthPx: number): number {
  const w = Number.isFinite(pageWidthPx) && pageWidthPx > 0 ? pageWidthPx : 0;
  return gapFraction(mm) * w;
}

/**
 * Screen, when the page width is a CSS expression rather than a number
 * (the flow's cards are `min(44vw, 300px)` wide). Returns a `calc()`.
 */
export function gapCss(mm: number, pageWidthCss: string): string {
  const f = gapFraction(mm);
  if (f === 0) return '0px';
  return `calc(${pageWidthCss} * ${f})`;
}

/**
 * Slot aspect ratios (w / h) for a layout at a given gap, from the SAME
 * geometry the print uses.
 *
 * ⚠️ THIS REPLACES A HARDCODED TABLE. `SLOT_AR_BY_LAYOUT` in the editor was
 * precomputed with a 2.5 mm gutter that the print never had; every crop and
 * face-centring decision read it. The moment the gap becomes a setting, a
 * fixed table is wrong for every value but one — so the ratios are derived.
 */
export function slotArs(layout: string, mm: number, topFrac?: number | null, baseRules?: boolean): number[] {
  // Both sides of a spread have the same 27 mm of width margin, so the frame of
  // page 0 gives every page's slot sizes.
  return slotRects(layout, topFrac, PAGE_W_MM, PAGE_H_MM, clampGapMm(mm), baseRules ? baseFrameMm(0) : null)
    .map((r) => r.w / r.h);
}

/** Slot size as fractions of the page, for print-quality (DPI) maths. */
export function slotFractions(layout: string, slotIndex: number, mm: number, topFrac?: number | null, baseRules?: boolean): { wFrac: number; hFrac: number } {
  const rects = slotRects(layout, topFrac, PAGE_W_MM, PAGE_H_MM, clampGapMm(mm), baseRules ? baseFrameMm(0) : null);
  const r = rects[slotIndex] ?? rects[0];
  return { wFrac: r.w / PAGE_W_MM, hFrac: r.h / PAGE_H_MM };
}

export { WIDE_TOP_MIN_FRAC, WIDE_TOP_MAX_FRAC };

/**
 * Flex style for row `rowIdx` of a two-row page whose top row is `topFrac` of
 * the page (3-up 0.6, wide-top its band) with a `gapPx` gap between the rows.
 *
 * ⚠️ PRINT TRUTH: slotGeometry gives the top row `topFrac × H − gap/2` and the
 * bottom row the rest. `flex: 0.6 / 0.4` split (H − gap) instead — 0.6 × gap off,
 * invisible at 0 mm and 0.4 mm wrong at the base rules' 4 mm. A fixed-basis top
 * row and a growing bottom row reproduce the print exactly.
 */
/**
 * 2-up-even (base rules): each row at the photos' height (`top_h_frac` of the
 * page's inside), the spare height shared evenly above, between and below —
 * `justify-content: space-evenly` with no gap is exactly slotRects' (H − 2h)/3.
 */
export function evenRowPct(topFrac: number | null | undefined): number {
  return evenFrac(topFrac, PAGE_H_MM - 2 * BASE_MARGIN_MM, BASE_GAP_MM) * 100;
}
export const EVEN_CONTAINER = { gap: 0, justifyContent: 'space-evenly' } as const;

export function twoRowFlex(topFrac: number, rowIdx: number, gapPx: number): { flex: string; minHeight: number } {
  return rowIdx === 0
    ? { flex: `0 0 calc(${topFrac * 100}% - ${gapPx / 2}px)`, minHeight: 0 }
    : { flex: '1 1 0%', minHeight: 0 };
}

/** `twoRowFlex` when the gap is a CSS expression (the flow's cards). */
export function twoRowFlexCss(topFrac: number, rowIdx: number, gapCssValue: string): { flex: string; minHeight: number } {
  return rowIdx === 0
    ? { flex: `0 0 calc(${topFrac * 100}% - (${gapCssValue}) / 2)`, minHeight: 0 }
    : { flex: '1 1 0%', minHeight: 0 };
}
